import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Scissors, Loader2 } from "lucide-react";
import { CropOverlay } from "./CropOverlay";
import { toast } from "sonner";
import { useOverlayPortal } from "../../lib/overlayPortal";

export const canCaptureScreen = () => typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;

/* 抓一帧屏幕（浏览器原生选择：整个屏幕 / 窗口 / 标签页）→ 源 canvas；抓帧期间隐藏沟通窗（body[data-capturing]） */
async function grabFrame() {
  document.body.setAttribute("data-capturing", "1");
  let stream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 5 }, audio: false, selfBrowserSurface: "include", surfaceSwitching: "exclude", systemAudio: "exclude" });
    const video = document.createElement("video");
    video.srcObject = stream; video.muted = true; video.playsInline = true;
    await video.play();
    if (!video.videoWidth) await new Promise((r) => { video.onloadedmetadata = r; setTimeout(r, 1500); });
    await new Promise((r) => setTimeout(r, 350));
    const c = document.createElement("canvas");
    c.width = video.videoWidth; c.height = video.videoHeight;
    c.getContext("2d").drawImage(video, 0, 0);
    return c;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
    document.body.removeAttribute("data-capturing");
  }
}

const ts = () => { const d = new Date(), p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`; };

/* 截图按钮：抓屏 → 全屏裁剪层（拖框选区 · 确定 / 全图 / 重截 / 取消 · Esc / Enter）→ PNG 文件回调给输入框作为图片附件 */
export const ScreenCapture = ({ zh, disabled, onCapture, className }) => {
  const portal = useOverlayPortal(); // 裁剪工具必须显示在沟通面板上方。
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const T = (z, e) => (zh ? z : e);
  const start = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const c = await grabFrame();
      if (!c.width) throw new Error("empty");
      const url = await new Promise((r) => c.toBlob((b) => r(URL.createObjectURL(b)), "image/png"));
      setShot({ canvas: c, url });
    } catch (e) {
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") toast.error(T("截图失败：浏览器未授权或不支持（可用系统截图后 Ctrl+V 粘贴）", "Screenshot failed — try a system screenshot and paste with Ctrl+V"));
    }
    setBusy(false);
  };
  const close = () => { if (shot) URL.revokeObjectURL(shot.url); setShot(null); };
  if (!canCaptureScreen()) return null;
  return (
    <>
      <button type="button" data-testid="chat-screenshot-btn" title={T("截图（框选屏幕区域后作为图片发送）", "Screenshot (select an area to attach)")} disabled={disabled || busy} onClick={start} className={className}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />}
      </button>
      {shot && createPortal(<CropOverlay shot={shot} zh={zh} onRetake={() => { close(); setTimeout(start, 50); }} onCancel={close}
        onDone={(blob) => { onCapture(new File([blob], `screenshot_${ts()}.png`, { type: "image/png" })); close(); }} />, portal || document.body)}
    </>
  );
};
